import {
  ClickAwayListener,
  Grid,
  Grow,
  IconButton,
  Link,
  MenuItem,
  MenuList,
  Popper,
  styled,
} from '@material-ui/core';
import { NavigateNext } from '@material-ui/icons';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { last } from 'lodash-es';
import React from 'react';
import { useBoolean } from 'react-use-boolean';
import { BreadcrumbData } from '../../hooks/usePathFromHierarchy';
import { FontStyle } from './FontStyle';
import { Paper } from './Paper';
import { Typography } from './Typography';

const LocalTypography = styled(Typography)({ fontSize: '20px' });
const LocalFontStyle = styled(FontStyle)({ fontSize: '20px' });

export interface BreadcrumbsProps {
  breadcrumbData: BreadcrumbData;
  makeLink: (guid: string) => string;
  maxItems?: number;
}

const BreadCrumbsLinkItem: React.FC<{ name?: string; link: string }> = ({ name, link }) => (
  <LocalTypography color='textPrimary' paragraph={false}>
    <Link color='inherit' href={link}>
      {name}
    </Link>
  </LocalTypography>
);

const BreadcrumbsEndLine: React.FC<Pick<BreadcrumbsProps, 'breadcrumbData' | 'makeLink'>> = ({
  breadcrumbData,
  makeLink,
}) => (
  <>
    {breadcrumbData.slice(0, -1).map(({ guid, name }) => [
      <Grid item key={guid + 'name'}>
        <BreadCrumbsLinkItem link={makeLink(guid)} name={name} />
      </Grid>,
      <Grid item key={guid + 'next'}>
        <NavigateNext fontSize='large' />
      </Grid>,
    ])}
    <Grid item aria-current='location' role='link' aria-disabled>
      <LocalFontStyle color='textPrimary' paragraph={false} bold>
        {last(breadcrumbData)?.name}
      </LocalFontStyle>
    </Grid>
  </>
);

export const Breadcrumbs: React.FC<BreadcrumbsProps> = ({
  breadcrumbData,
  makeLink,
  maxItems = 3,
}) => {
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [open, actions] = useBoolean();

  if (breadcrumbData.length <= maxItems) {
    return (
      <nav aria-label='breadcrumb'>
        <Grid container alignItems={'center'}>
          <BreadcrumbsEndLine breadcrumbData={breadcrumbData} makeLink={makeLink} />
        </Grid>
      </nav>
    );
  }
  return (
    <nav aria-label='breadcrumb'>
      <Grid container alignItems={'center'}>
        <Grid item>
          <BreadCrumbsLinkItem link={makeLink(breadcrumbData[0].guid)} {...breadcrumbData[0]} />
        </Grid>
        <Grid item>
          <NavigateNext fontSize='large' />
        </Grid>
        <Grid item>
          <IconButton
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup='true'
            aria-label={`open action menu`}
            size={'small'}
            onClick={actions.on}
          >
            <MoreHorizIcon />
          </IconButton>
          <Popper open={open} anchorEl={anchorRef.current} transition>
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                  zIndex: 9999,
                }}
              >
                <Paper variant='outlined'>
                  <ClickAwayListener onClickAway={actions.off}>
                    <MenuList autoFocusItem={open} id='menu-list-grow'>
                      {breadcrumbData.slice(1, -maxItems + 1).map(({ name, guid }) => (
                        <Link key={guid} color='inherit' href={makeLink(guid)}>
                          <MenuItem onClick={actions.off}>{name}</MenuItem>
                        </Link>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
        </Grid>
        <Grid item>
          <NavigateNext fontSize='large' />
        </Grid>
        <BreadcrumbsEndLine
          breadcrumbData={breadcrumbData.slice(-maxItems + 1)}
          makeLink={makeLink}
        />
      </Grid>
    </nav>
  );
};
